import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { queryClient } from 'src/query-client';
import { Checkbox } from 'antd';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import { useCurrentWorkspace } from '@hooks/workspace';
import useMarqueeSelection from '@hooks/useMarqueeSelection';
import AssetsEmptyState from '@components/Assets/AssetsEmptyState';
import { moveAssetToFolder } from '@redux/actions/assetsAction';
import { moveFolder } from '@api/Asset';
import { AssetsList, FoldersList } from '@components/Assets';
import BulkActions from '@components/Assets/BulkActions';
import {
  toggleCampaignFolderCreationModal,
  toggleMoveAssetToPrivateFolder,
  toggleUploadAssetModal
} from '@redux/actions/modalAction';
import { useLayout } from '@layouts/PageLayout';
import { useDebounced } from '@helpers/useDebounced';
import { useAssetsQuery, useAssetsCountQuery } from '@hooks/queries/assets';
import { AssetItemDto, CampaignItemDto } from '@api/Api';
import { optimisticListItemRemove } from '@hooks/queries/helpers';
import { PlusIcon } from '@icons';

interface IAssetsTabProps {
  campaign: CampaignItemDto;
  orderBy: string;
  searchQuery: string | undefined;
  activeTabKey: string;
}

const assetsOrderParams = [
  'createdAt:ASC',
  'createdAt:DESC',
  'modifiedAt:ASC',
  'modifiedAt:DESC',
  'deletedAt:ASC',
  'deletedAt:DESC',
  'openedAt:ASC',
  'openedAt:DESC',
  'order:DESC',
  'name:ASC',
  'name:DESC'
] as const;

export default memo(function AssetsTab({
  campaign,
  searchQuery,
  activeTabKey,
  ...otherProps
}: IAssetsTabProps) {
  const [searchParams] = useSearchParams();
  const { currentView } = useLayout();
  const dispatch = useDispatch();
  const [workspace] = useCurrentWorkspace();

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const isTileView = currentView === 'tile';

  const orderBy: (typeof assetsOrderParams)[number][] | undefined = useMemo(
    () =>
      otherProps.orderBy &&
      assetsOrderParams.includes(otherProps.orderBy as any)
        ? [otherProps.orderBy as (typeof assetsOrderParams)[number]]
        : undefined,
    [otherProps.orderBy]
  );
  const debouncedSearchQuery = useDebounced(searchQuery, 250);

  const prevCampaignId = useRef(campaign.id);

  const flatten = !!debouncedSearchQuery;
  const assetsQuery = useAssetsQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: false,
      searchQuery: debouncedSearchQuery,
      orderBy
    },
    {
      enabled: activeTabKey === 'assets',
      keepPreviousData: campaign.id === prevCampaignId.current
    }
  );
  const assetsCountQuery = useAssetsCountQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: false,
      searchQuery: debouncedSearchQuery
    },
    {
      enabled: activeTabKey === 'assets',
      keepPreviousData: campaign.id === prevCampaignId.current,
      placeholderData: campaign.assetsCount
    }
  );

  const foldersQuery = useAssetsQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: true,
      searchQuery: debouncedSearchQuery,
      orderBy
    },
    {
      enabled: activeTabKey === 'assets',
      keepPreviousData: campaign.id === prevCampaignId.current
    }
  );
  const foldersCountQuery = useAssetsCountQuery(
    {
      workspaceId: campaign.workspaceId as any,
      campaignId: campaign.id as any,
      flatten,
      isFolder: true,
      searchQuery: debouncedSearchQuery
    },
    {
      enabled: activeTabKey === 'assets',
      keepPreviousData: campaign.id === prevCampaignId.current,
      placeholderData: campaign.foldersCount
    }
  );

  if (campaign.id !== prevCampaignId.current) {
    prevCampaignId.current = campaign.id;
  }

  const assetsList = useMemo(() => {
    return assetsQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [assetsQuery.data]);

  const foldersList = useMemo(() => {
    return foldersQuery.data?.pages.flatMap((page) => page.edges) || [];
  }, [foldersQuery.data]);

  const assetsCount = assetsCountQuery.data ?? 0;
  const foldersCount = foldersCountQuery.data ?? 0;
  const assetsToFetch = Math.max(0, assetsCount - assetsList.length);
  const foldersToFetch = Math.max(0, foldersCount - foldersList.length);

  const [selectedFolders, setSelectedFolders] = useState<
    { node: AssetItemDto; cursor: string }[]
  >([]);
  const [selectedAssets, setSelectedAssets] = useState<
    { node: AssetItemDto; cursor: string }[]
  >([]);

  const marqueeContainerRef = useRef<HTMLDivElement>(null);
  const foldersListRef = useRef(foldersList);
  foldersListRef.current = foldersList;
  const assetsListRef = useRef(assetsList);
  assetsListRef.current = assetsList;

  const onMarqueeSelect = useCallback(
    (items: { id: string; type: string }[]) => {
      if (!items.length) return;

      const folderIds = new Set(
        items.filter((item) => item.type === 'folder').map((item) => item.id)
      );
      const assetIds = new Set(
        items.filter((item) => item.type === 'asset').map((item) => item.id)
      );

      if (folderIds.size) {
        setSelectedFolders((prev) => {
          const byId = new Map(prev.map((folder) => [folder.node.id, folder]));
          foldersListRef.current.forEach((folder) => {
            if (folderIds.has(folder.node.id)) {
              byId.set(folder.node.id, folder);
            }
          });
          return [...byId.values()];
        });
      }

      if (assetIds.size) {
        setSelectedAssets((prev) => {
          const byId = new Map(prev.map((asset) => [asset.node.id, asset]));
          assetsListRef.current.forEach((asset) => {
            if (assetIds.has(asset.node.id)) {
              byId.set(asset.node.id, asset);
            }
          });
          return [...byId.values()];
        });
      }
    },
    []
  );

  const { marqueeBox } = useMarqueeSelection({
    containerRef: marqueeContainerRef,
    enabled: true,
    onSelect: onMarqueeSelect
  });

  const allowUpload = !!campaign.permissions.createAssets && !campaign.demo;
  const showEmptyState = allowUpload || !campaign.foldersCount;

  return (
    <div
      ref={marqueeContainerRef}
      className="marquee-selection-container"
      style={{
        paddingRight: 34,
        paddingLeft: 34,
        marginRight: -34,
        marginLeft: -34,
        height: 'calc(100vh - 288px - var(--demo-mode-panel-width, 0px))',
        overflow: 'auto'
      }}
    >
      {marqueeBox}
      <div className="campaign_items">
        <div className="campaign_items__top">
          <button
            type="button"
            className="campaign_items__title"
            disabled={!allowUpload}
            onClick={() => {
              if (!allowUpload) return;
              dispatch(
                toggleCampaignFolderCreationModal({
                  entity: 'folder',
                  campaignId: campaign.id,
                  visible: true
                })
              );
            }}
          >
            {campaign.foldersCount ? 'Folders' : 'Add folder'}{' '}
            {allowUpload && <PlusIcon />}
          </button>
          {!!foldersList?.length && isTileView && (
            <Checkbox
              checked={
                !!selectedFolders.length &&
                selectedFolders.length === foldersCount
              }
              indeterminate={
                !!selectedFolders.length &&
                selectedFolders.length < foldersCount
              }
              onChange={async () => {
                if (selectedFolders.length) {
                  setSelectedFolders([]);
                  return;
                }
                let query = foldersQuery;
                while (query.hasNextPage) {
                  query = await query.fetchNextPage();
                }
                setSelectedFolders(
                  query.data?.pages.flatMap((x) => x.edges) ?? []
                );
              }}
            >
              Select all
            </Checkbox>
          )}
        </div>

        <InfiniteScroll
          loading={foldersQuery.isFetchingNextPage ? 'more' : false}
          next={foldersQuery.fetchNextPage}
          hasMore={foldersQuery.hasNextPage ?? false}
        >
          <FoldersList
            canShare={campaign.permissions.createReviews}
            selectedFolders={selectedFolders}
            foldersCount={foldersCount}
            setSelectedFolders={async (ids, folders, isAllSelect) => {
              let query = foldersQuery;
              if (isAllSelect && ids.length) {
                while (query.hasNextPage) {
                  query = await query.fetchNextPage();
                }
              }
              setSelectedFolders((prev) => {
                const isDeselect =
                  isAllSelect &&
                  (!ids.length || prev.some((x) => ids.includes(x.node.id)));
                if (isDeselect) return [];
                if (isAllSelect) {
                  return query.data?.pages.flatMap((x) => x.edges) ?? [];
                }
                const newFolders = folders.filter(
                  (x) => !prev.some((y) => x.node.id === y.node.id)
                );
                return [...prev, ...newFolders].filter((x) =>
                  ids.includes(x.node.id)
                );
              });
            }}
            foldersList={foldersList}
            countToFetch={
              foldersQuery.isLoading || foldersQuery.isFetchingNextPage
                ? foldersToFetch
                : 0
            }
            isCardViewMode={isTileView}
            canReorganize={otherProps.orderBy === 'order:DESC'}
            canDropInside={true}
            listLoading={foldersQuery.isLoading}
            onAssetDrop={(assetId, folderId) => {
              const asset = assetsList
                .map((x) => x.node)
                .find((x) => x.id === assetId);
              const folder = foldersList
                .map((x) => x.node)
                .find((x) => x.id === folderId);
              if (!asset?.asset || !folder?.folder) return;
              const { permissions } = asset.asset;
              const canMove =
                (folder.isPrivate && permissions.moveToPrivateFolder) ||
                (!folder.isPrivate && permissions.moveToPublicFolder);
              if (!canMove) return;
              const version = asset.asset.versions[0];
              const onConfirm = () =>
                dispatch(moveAssetToFolder({ assetId, folderId }, true));
              if (folder.isPrivate) {
                dispatch(
                  toggleMoveAssetToPrivateFolder({
                    visible: true,
                    folderName: folder.folder.name,
                    assetsName: [`${version.name}.${version.extension}`],
                    callback: onConfirm
                  })
                );
              } else onConfirm();
            }}
            onFolderDrop={async (sourceFolderId, targetFolderId) => {
              if (sourceFolderId === targetFolderId) return;
              const sourceFolder = foldersList
                .map((x) => x.node)
                .find((x) => x.id === sourceFolderId);
              const targetFolder = foldersList
                .map((x) => x.node)
                .find((x) => x.id === targetFolderId);
              if (!sourceFolder?.folder || !targetFolder?.folder) return;
              const { permissions } = sourceFolder.folder;
              const canMove = targetFolder.isPrivate
                ? permissions.moveToPrivateFolder
                : permissions.moveToPublicFolder;
              if (!canMove) return;
              const onConfirm = async () => {
                await queryClient.cancelQueries({ queryKey: ['assets'] });
                optimisticListItemRemove<AssetItemDto>({
                  queryKey: ['assets', 'list'],
                  id: sourceFolderId
                });
                await moveFolder({
                  folderId: sourceFolderId,
                  targetParentId: targetFolderId
                });
                await queryClient.invalidateQueries({ queryKey: ['assets'] });
              };
              if (!sourceFolder.isPrivate && targetFolder.isPrivate) {
                dispatch(
                  toggleMoveAssetToPrivateFolder({
                    visible: true,
                    folderName: targetFolder.folder.name,
                    assetsName: [sourceFolder.folder.name],
                    callback: onConfirm
                  })
                );
              } else {
                await onConfirm();
              }
            }}
          />
        </InfiniteScroll>

        {assetsCount ? (
          <InfiniteScroll
            loading={assetsQuery.isFetchingNextPage ? 'more' : false}
            next={assetsQuery.fetchNextPage}
            hasMore={assetsQuery.hasNextPage ?? false}
          >
            <div className="campaign_items__top">
              <button
                type="button"
                className="campaign_items__title"
                onClick={() => {
                  if (!allowUpload) return;
                  dispatch(
                    toggleUploadAssetModal({
                      visible: true,
                      workspaceId: workspace.id,
                      campaignId: campaign.id
                    })
                  );
                }}
              >
                Media {allowUpload && <PlusIcon />}
              </button>
              {!!assetsList?.length && isTileView && (
                <Checkbox
                  checked={
                    !!selectedAssets.length &&
                    selectedAssets.length === assetsCount
                  }
                  indeterminate={
                    !!selectedAssets.length &&
                    selectedAssets.length < assetsCount
                  }
                  onChange={async () => {
                    if (selectedAssets.length) {
                      setSelectedAssets([]);
                      return;
                    }
                    let query = assetsQuery;
                    while (query.hasNextPage) {
                      query = await query.fetchNextPage();
                    }
                    setSelectedAssets(
                      query.data?.pages.flatMap((x) => x.edges) ?? []
                    );
                  }}
                >
                  Select all
                </Checkbox>
              )}
            </div>

            <AssetsList
              campaign={campaign}
              searchQuery={searchQuery || null}
              selectedAssets={selectedAssets}
              setSelectedAssets={async (ids, assets, isAllSelect) => {
                let query = assetsQuery;
                if (isAllSelect && ids.length) {
                  while (query.hasNextPage) {
                    query = await query.fetchNextPage();
                  }
                }
                setSelectedAssets((prev) => {
                  const isDeselect =
                    isAllSelect &&
                    (!ids.length || prev.some((x) => ids.includes(x.node.id)));
                  if (isDeselect) return [];
                  if (isAllSelect) {
                    return query.data?.pages.flatMap((x) => x.edges) ?? [];
                  }
                  const newAssets = assets.filter(
                    (x) => !prev.some((y) => x.node.id === y.node.id)
                  );
                  return [...prev, ...newAssets].filter((x) =>
                    ids.includes(x.node.id)
                  );
                });
              }}
              assetsList={assetsList}
              displayAssetsCount={assetsCount}
              isTileView={isTileView}
              page="campaign"
              countToFetch={assetsQuery.isFetchingNextPage ? assetsToFetch : 0}
              canReorganize={otherProps.orderBy === 'order:DESC'}
              canCombine={true}
              listLoading={assetsQuery.isLoading}
              canShare={campaign.permissions.createReviews}
              isShowCompany={false}
            />
          </InfiniteScroll>
        ) : (
          showEmptyState && (
            <AssetsEmptyState campaign={campaign} isSearchEmptyState={false} />
          )
        )}
        <BulkActions
          selectedAssets={selectedAssets}
          selectedFolders={selectedFolders}
          canShare={campaign.permissions.createReviews}
          page="campaign"
          onRefreshAssetList={assetsQuery.refetch}
          onClose={() => {
            setSelectedAssets([]);
            setSelectedFolders([]);
          }}
        />
      </div>
    </div>
  );
});
